import {useMemo, useEffect, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {addMonths, addYears, subMonths, subYears, isSameMonth} from 'date-fns';
import {PageWrapper, PageHeader, PageContentWrapper, FABWrapper} from '../../Layout';
import {FAB} from '../../components/UI/Button/FAB';
import CalendarControls from '../../components/UI/Calendar/CalendarControls';
import {useMonthReport} from '../../store/dailyReport/dailyReportSlice';
import {
    getCleanUpByDate,
    cleanUps as cleanUpsSelector,
    loadedDates,
    removeCleanUp,
} from '../../store/cleanUp/cleanUpSlice';
import {useAppDispatch, useAppSelector} from '../../store/app-hooks';
import {useProgramsData} from '../../store/programs/programsSlice';
import {deleteCleanUp} from '../../api/cleanUp';
import {ICleanUp} from '../../models/cleanUp';
import {dateToMonthString, dateToString, convertDateToString} from '../../utils/date_helpers';
import s from './index.module.scss';

export const CleanUps = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [params, setParams] = useSearchParams();
    const monthString = params.get('date') ?? dateToMonthString(new Date());
    const selectedDate = new Date(monthString);

    const [deleteTarget, setDeleteTarget] = useState<ICleanUp | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    useEffect(() => {
        if (!params.get('date')) {
            setParams({date: dateToMonthString(new Date())});
        }
    });

    // Load month summary (gives us which dates have clean-ups)
    const [monthReport, reportLoaded] = useMonthReport(monthString);

    // Fetch per-day clean-up data for each date that has clean-ups and isn't loaded yet
    const alreadyLoadedDates = useAppSelector(loadedDates);
    useEffect(() => {
        if (!reportLoaded) return;
        monthReport.clean_ups.forEach((item) => {
            const dateStr = dateToString(new Date(item.date));
            if (!alreadyLoadedDates.includes(dateStr)) {
                dispatch(getCleanUpByDate(dateStr));
            }
        });
    }, [reportLoaded, monthString]); // eslint-disable-line react-hooks/exhaustive-deps

    // Filter Redux store to this month, sorted newest-first
    const allCleanUps = useAppSelector(cleanUpsSelector);
    const monthlyCleanUps = useMemo(() => {
        return [...allCleanUps]
            .filter((cu) => isSameMonth(new Date(cu.date), selectedDate))
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }, [allCleanUps, monthString]); // eslint-disable-line react-hooks/exhaustive-deps

    // Name resolution
    const [programs] = useProgramsData();
    const resolveProgram = (programId: string) =>
        programs.find((p) => p.id === programId)?.name ?? '—';

    const resolveActivity = (programId: string, activityId: string | null) => {
        if (!activityId) return '—';
        const prog = programs.find((p) => p.id === programId);
        return prog?.activities.find((a) => a.id === activityId)?.name ?? '—';
    };

    const resolveCollector = (createdBy: ICleanUp['created_by']) => {
        if (!createdBy) return '—';
        return `${createdBy.first_name} ${createdBy.last_name}`;
    };

    const totalWaste = (cu: ICleanUp) =>
        cu.collected_weights.reduce((sum, w) => sum + (w.wet ?? 0) + (w.dry ?? 0), 0).toFixed(1);

    // Determine if all dates are loaded
    const datesInReport = monthReport.clean_ups.map((item) => dateToString(new Date(item.date)));
    const dataLoaded =
        reportLoaded && datesInReport.every((d) => alreadyLoadedDates.includes(d));

    const setDate = (date: Date) => setParams({date: dateToMonthString(date)});

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        setDeleteError(null);
        try {
            await deleteCleanUp(deleteTarget.id);
            dispatch(removeCleanUp(deleteTarget.id));
            setDeleteTarget(null);
        } catch {
            setDeleteError('Failed to delete. Please try again.');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <PageWrapper>
            <PageHeader title="Clean-ups Management" />
            <PageContentWrapper>
                <div className={s.container}>
                    <CalendarControls
                        selectedDate={selectedDate}
                        onPrevMonth={() => setDate(subMonths(selectedDate, 1))}
                        onNextMonth={() => setDate(addMonths(selectedDate, 1))}
                        onPrevYear={() => setDate(subYears(selectedDate, 1))}
                        onNextYear={() => setDate(addYears(selectedDate, 1))}
                    />

                    <div className={s.tableContainer}>
                        {!dataLoaded ? (
                            <div className={s.stateRow}>Loading clean-ups...</div>
                        ) : (
                            <table className={s.table}>
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Program</th>
                                        <th>Location</th>
                                        <th>Activity</th>
                                        <th>Collector</th>
                                        <th>Waste (kg)</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {monthlyCleanUps.length > 0 ? (
                                        monthlyCleanUps.map((cu) => (
                                            <tr key={cu.id}>
                                                <td>
                                                    {convertDateToString(new Date(cu.date))}
                                                </td>
                                                <td>{resolveProgram(cu.program)}</td>
                                                <td>
                                                    {cu.location_name ||
                                                        `${cu.location?.latitude}, ${cu.location?.longitude}`}
                                                </td>
                                                <td>{resolveActivity(cu.program, cu.activity)}</td>
                                                <td>{resolveCollector(cu.created_by)}</td>
                                                <td>{totalWaste(cu)}</td>
                                                <td className={s.actions}>
                                                    <button
                                                        onClick={() =>
                                                            navigate(`/clean-ups/edit/${cu.id}`, {
                                                                state: {returnDate: monthString},
                                                            })
                                                        }
                                                        className={s.editButton}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setDeleteError(null);
                                                            setDeleteTarget(cu);
                                                        }}
                                                        className={s.deleteButton}
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={7} className={s.noResults}>
                                                No clean-ups found for this month
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>

                {/* Delete confirmation modal */}
                {deleteTarget && (
                    <div className={s.modalOverlay}>
                        <div className={s.modal}>
                            <h3 className={s.modalTitle}>Delete Clean-up</h3>
                            <p className={s.modalBody}>
                                Are you sure you want to delete the clean-up for{' '}
                                <strong>
                                    {resolveProgram(deleteTarget.program)} on{' '}
                                    {convertDateToString(new Date(deleteTarget.date))}
                                </strong>
                                ? This action cannot be undone.
                            </p>
                            {deleteError && <p className={s.modalError}>{deleteError}</p>}
                            <div className={s.modalActions}>
                                <button
                                    className={s.modalCancel}
                                    onClick={() => setDeleteTarget(null)}
                                    disabled={deleting}
                                >
                                    Cancel
                                </button>
                                <button
                                    className={s.modalConfirm}
                                    onClick={handleConfirmDelete}
                                    disabled={deleting}
                                >
                                    {deleting ? 'Deleting...' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </PageContentWrapper>

            <FABWrapper>
                <FAB onClick={() => navigate('/clean-ups/create', {state: {returnDate: monthString}})} />
            </FABWrapper>
        </PageWrapper>
    );
};
